import React, { useState } from 'react';
import type { Node } from 'react';
import { themeProp } from '@vmos2/utils';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import get from 'lodash/get';
import { useDeviceSize } from '@vmos2/providers';

import { Modal, Button, Text } from '..';

import { useComponentTheme } from '../useComponentTheme';

type Props = {
  name: string,
  hideChangeButton: boolean,
  isMobile: boolean,
  onClick: Function,
  hasBundles: boolean,
};

export const StoreSelection = ({ name, isMobile, onClick, hideChangeButton, hasBundles }: Props): Node => {
  const componentTheme = useComponentTheme(theme => theme.components.Header, 'Main');
  const { isLarge } = useDeviceSize();
  const { t } = useTranslation('header');
  const { t: tCommon } = useTranslation('common');
  const [isOpened, setIsOpened] = useState(false);

  return (
    <Container componentTheme={componentTheme} data-test="store-selection">
      <TextWrapper componentTheme={componentTheme}>{name}</TextWrapper>
      {!hideChangeButton && (
        <ButtonWrapper
          componentTheme={componentTheme}
          onClick={() => (hasBundles ? setIsOpened(true) : onClick())}
          mode="link"
          data-test="change-store"
        >
          {t('change')}
        </ButtonWrapper>
      )}
      <Modal
        onRequestClose={() => setIsOpened(false)}
        hasWrapperMargin={false}
        shouldCloseOnOverlayClick
        size="small"
        title={t('discardWarningTitle')}
        isOpen={isOpened}
        footerSections={1}
        footerRightSide={
          <>
            <Button
              contentCenter
              block
              mode="light"
              size="large"
              onClick={() => setIsOpened(false)}
              mr={!isLarge ? 3 : 4}
              ml={!isLarge ? 2 : 3}
            >
              {isMobile ? tCommon('no') : t('discardWarningCancel')}
            </Button>
            <Button contentCenter block size="large" onClick={() => onClick()} mr={!isLarge ? 2 : 3}>
              {isMobile ? tCommon('yes') : t('discardWarningContinue')}
            </Button>
          </>
        }
      >
        <Text display="block" level={!isLarge ? '3' : '2'} mb={4} mt={2}>
          {t('discardWarningText')}
        </Text>
      </Modal>
    </Container>
  );
};

StoreSelection.defaultProps = {
  hideChangeButton: false,
};

const Container = styled('div')`
  margin-left: 24px;

  @media (${themeProp('breakpoint.tabletLarge')}) {
    margin-left: 16px;
  }
`;

const ButtonWrapper = styled(Button)`
  color: ${themeProp('color.brand')};

  @media (${themeProp('breakpoint.tabletLarge')}) {
    color: ${({ theme, componentTheme }) => get(componentTheme, 'Link.color', theme.color.brand)};
  }
`;

const TextWrapper = styled('span')`
  color: ${themeProp('color.black')};
  margin-right: 8px;

  @media (${themeProp('breakpoint.tabletLarge')}) {
    color: ${({ componentTheme }) => componentTheme.Text.color};
  }
`;
